import { HStack } from '@chakra-ui/react';
import React from 'react';

import {
  OptimalDayItems,
  OptimalDayItem,
} from 'features/challenge/hooks/mutation/useEditOptimalDay';
import {
  OptimalDayValue,
  OPTIMAL_DAY_VALUES,
  OPTIMAL_DAY_ICONS,
} from 'features/challenge/hooks/useOptimalDay';

export const createOptimalDayPayload = (
  targetDate: string,
  challengeItems: OptimalDayItem[]
): OptimalDayItems => {
  return {
    target_date: targetDate,
    payload: {
      items: challengeItems,
    },
  };
};

export const createChallengeItem = (
  challengeId: string,
  optimalValues: OptimalDayValue[],
  previousDisplayBuckets: Record<string, number> = {}
): OptimalDayItem => {
  const display_buckets: Record<string, number> = {};

  optimalValues.forEach((value) => {
    display_buckets[value] = previousDisplayBuckets[value] ?? 0;
  });

  return {
    item_id: challengeId,
    display_buckets,
  };
};

/**
 * Sort optimal day values in the correct order: morning -> day -> evening
 */
export const sortOptimalDayValues = (values: string[]): string[] => {
  const order = [
    OPTIMAL_DAY_VALUES.MORNING,
    OPTIMAL_DAY_VALUES.DAY,
    OPTIMAL_DAY_VALUES.EVENING,
  ];
  return values.sort(
    (a, b) =>
      order.indexOf(a as OptimalDayValue) - order.indexOf(b as OptimalDayValue)
  );
};

/**
 * Check if two arrays of optimal day values are equal (order-independent)
 */
export const areOptimalDayValuesEqual = (
  values1: OptimalDayValue[],
  values2: OptimalDayValue[]
): boolean => {
  if (values1.length !== values2.length) return false;

  const sorted1 = sortOptimalDayValues([...values1]);
  const sorted2 = sortOptimalDayValues([...values2]);

  return sorted1.every((val, idx) => val === sorted2[idx]);
};

/**
 * Render optimal day icons for given values
 */
export const renderOptimalDayIcons = (
  values: OptimalDayValue[],
  size: number = 16,
  showSkip: boolean = true,
  isGrayed: boolean = false
): React.ReactNode => {
  if (values.length === 0) {
    if (!showSkip) return null;

    return React.createElement(
      'span',
      isGrayed ? { style: { color: 'gray' } } : {},
      'skip'
    );
  }

  const sortedValues = sortOptimalDayValues([...values]);

  const icons = sortedValues.map((value) => {
    const Icon = OPTIMAL_DAY_ICONS[value as OptimalDayValue];
    return Icon ? React.createElement(Icon, { key: value, size }) : null;
  });

  return React.createElement(
    HStack,
    {
      gap: 1,
      ...(isGrayed && { style: { color: 'gray' } }),
    },
    icons
  );
};
